package com.sweetlime.dungeonamigo.repository

import com.sweetlime.dungeonamigo.model.Character
import com.sweetlime.dungeonamigo.model.SpellSlotLevel
import com.sweetlime.dungeonamigo.util.currentTimeMillis
import dev.gitlive.firebase.firestore.FirebaseFirestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

class FirebaseCharacterRepository(
    private val firestore: FirebaseFirestore,
) : CharacterRepository {

    private fun col(userId: String) =
        firestore.collection("users").document(userId).collection("characters")

    override fun getCharacters(userId: String): Flow<List<Character>> =
        col(userId).snapshots.map { snapshot ->
            snapshot.documents.mapNotNull { doc ->
                runCatching { doc.data<Character>() }.getOrNull()
            }
        }

    override suspend fun getCharacter(userId: String, characterId: String): Character? =
        runCatching {
            col(userId).document(characterId).get().data<Character>()
        }.getOrNull()

    override suspend fun createCharacter(character: Character): String {
        val ref = col(character.userId).document
        val now = currentTimeMillis()
        ref.set(character.copy(id = ref.id, createdAt = now, updatedAt = now))
        return ref.id
    }

    override suspend fun updateCharacter(character: Character) {
        col(character.userId).document(character.id)
            .set(character.copy(updatedAt = currentTimeMillis()))
    }

    override suspend fun updateHp(userId: String, characterId: String, currentHp: Int, temporaryHp: Int) {
        col(userId).document(characterId).update(
            "currentHp" to currentHp,
            "temporaryHp" to temporaryHp,
            "updatedAt" to currentTimeMillis().toDouble(),
        )
    }

    override suspend fun updateConditions(userId: String, characterId: String, conditions: List<String>) {
        col(userId).document(characterId).update(
            "conditions" to conditions,
            "updatedAt" to currentTimeMillis().toDouble(),
        )
    }

    override suspend fun updateSpellSlots(userId: String, characterId: String, slots: Map<String, SpellSlotLevel>) {
        col(userId).document(characterId).update(
            "spellSlots" to slots,
            "updatedAt" to currentTimeMillis().toDouble(),
        )
    }

    override suspend fun deleteCharacter(userId: String, characterId: String) {
        col(userId).document(characterId).delete()
    }

    override suspend fun getCharacterCount(userId: String): Int =
        runCatching { col(userId).get().documents.size }.getOrDefault(0)

}
